import type { ScoreInput, WorkerScore } from '../index.js'
import type { ShiftOutcome } from '../../partner/index.js'
import type { WorkerNotification } from '../../messages/index.js'
import { latestPerShift, notificationMonth } from './monthPanelData.js'

/**
 * What one scoring period's view is built from, whichever period it is:
 * the period's score read, the rows of its breakdown, the shifts reported
 * in it and what the worker was told about it. The current period gets
 * these from the live reads; a past month from the period score and the
 * same outcomes and notifications, narrowed to that month.
 */
export interface PeriodData {
  /** `YYYY-MM`. */
  month: string
  /** The period's score, or null when there is none for it. */
  score: WorkerScore | null
  breakdown: readonly ScoreInput[]
  /** One standing record per shift, as the engine counts them. */
  shifts: readonly ShiftOutcome[]
  notifications: readonly WorkerNotification[]
}

/** The shifts dated in a month, one standing record each. */
export function shiftsInMonth(outcomes: readonly ShiftOutcome[], month: string): ShiftOutcome[] {
  return latestPerShift(outcomes.filter((outcome) => outcome.shiftDate.slice(0, 7) === month))
}

export function notificationsInMonth(notifications: readonly WorkerNotification[], month: string): WorkerNotification[] {
  return notifications.filter((notification) => notificationMonth(notification) === month)
}

/**
 * A past period's breakdown rows, from the engine result stored with its
 * score - the same fields the breakdown endpoint returns for the current
 * period, minus the coaching nudge, which is only kept for the running one.
 */
export function breakdownFromScore(score: WorkerScore | null): ScoreInput[] {
  if (!score) {
    return []
  }
  return score.result.inputs.map((input) => ({
    key: input.key,
    label: input.label,
    value: input.value ?? 0,
    weight: input.weight,
    nudge: null,
    included: input.included,
    excludedReason: input.excludedReason,
    measure: input.measure,
    numerator: input.numerator,
    denominator: input.denominator,
    pointsLost: input.pointsLost,
  }))
}
